import React, { useState, useEffect } from 'react';
import {
  Users, Building2, CreditCard, Clock, TrendingUp, AlertTriangle,
  RefreshCw, ChevronDown
} from 'lucide-react';
import { getAllUsers, getAllOrganizations } from '../../services/superAdminService';
import PRICING_CONFIG from '../../services/pricingConfig';
import { grantFoundingStatus, revokeFoundingStatus, updateOrgPlan } from '../../services/firestoreService';

// ============================================
// PLAN BADGE — clickable dropdown for SuperAdmin overrides
// ============================================
// Renders the existing plan badge but, on click, opens a dropdown with the
// other selectable plans. `founding` is intentionally excluded — granting
// Founding 100 has its own button (it has to maintain the counter / number).

const SELECTABLE_PLANS = ['trial', 'active', 'comped', 'cancelled'];

const PlanBadge = ({ org, planColors, onChanged, onToast }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const planStyle = planColors[org.plan] || planColors.trial;

  // Founding orgs: render the badge as plain (non-clickable) — to change a
  // founding org's plan, the SuperAdmin should Revoke first (which restores
  // the previous plan automatically).
  if (org.plan === 'founding') {
    return (
      <span
        className="px-2 py-1 rounded text-xs font-semibold"
        style={{ backgroundColor: planStyle.bg, color: planStyle.text }}
        title="Revoke Founding 100 to change this plan"
      >
        {org.plan}
      </span>
    );
  }

  const handlePick = async (newPlan) => {
    if (newPlan === org.plan) { setOpen(false); return; }
    if (busy) return;
    if (!window.confirm(`Change ${org.name} from "${org.plan}" to "${newPlan}"?`)) {
      setOpen(false);
      return;
    }
    setBusy(true);
    const res = await updateOrgPlan(org.id, newPlan);
    setBusy(false);
    setOpen(false);
    if (res.success) {
      onToast(`Plan changed to "${newPlan}" for ${org.name}.`);
      onChanged();
    } else {
      onToast(res.reason || 'Could not update plan.', 'error');
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        className="px-2 py-1 rounded text-xs font-semibold inline-flex items-center gap-1"
        style={{
          backgroundColor: planStyle.bg,
          color: planStyle.text,
          border: '1px solid ' + planStyle.text + '40',
          cursor: busy ? 'wait' : 'pointer',
        }}
        title="Change plan"
      >
        {org.plan}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          {/* Click-away catcher */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 20 }}
          />
          <div
            className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border"
            style={{ borderColor: '#E2E8F0', minWidth: 140, zIndex: 30 }}
          >
            {SELECTABLE_PLANS.map(p => {
              const ps = planColors[p] || planColors.trial;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => handlePick(p)}
                  className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                  style={{
                    color: p === org.plan ? '#071D39' : '#516469',
                    fontWeight: p === org.plan ? 600 : 400,
                    backgroundColor: p === org.plan ? '#F0F4F5' : 'transparent',
                  }}
                >
                  <span
                    className="inline-block px-2 py-0.5 rounded mr-2"
                    style={{ backgroundColor: ps.bg, color: ps.text, fontSize: 10, fontWeight: 600 }}
                  >
                    {p}
                  </span>
                  {p === org.plan ? '(current)' : ''}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================
// METRIC CARD
// ============================================

const MetricCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg" style={{ backgroundColor: color + '15' }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs font-semibold" style={{ color: '#89A8B1' }}>{label}</p>
        <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      </div>
    </div>
  </div>
);

// ============================================
// FOUNDING 100 PROGRESS DIAL
// ============================================

const Founding100Dial = ({ count }) => {
  const max = 100;
  const pct = Math.min((count / max) * 100, 100);
  const radius = 54;
  const stroke = 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const remaining = max - count;

  return (
    <div className="bg-white rounded-lg shadow p-5 flex flex-col items-center justify-center">
      <p className="text-xs font-semibold mb-3" style={{ color: '#89A8B1' }}>FOUNDING 100 PROGRESS</p>
      <div className="relative" style={{ width: 130, height: 130 }}>
        <svg width="130" height="130" viewBox="0 0 130 130">
          <circle cx="65" cy="65" r={radius} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
          <circle
            cx="65" cy="65" r={radius}
            fill="none" stroke="#805AD5" strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 65 65)"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color: '#071D39' }}>{count}</span>
          <span className="text-xs" style={{ color: '#89A8B1' }}>of {max}</span>
        </div>
      </div>
      <p className="text-sm font-semibold mt-3" style={{ color: '#805AD5' }}>
        {remaining} spot{remaining !== 1 ? 's' : ''} remaining
      </p>
    </div>
  );
};

// ============================================
// CONVERSION RATE CARD
// ============================================

const ConversionRateCard = ({ orgs }) => {
  const trialCount = orgs.filter(o => o.plan === 'trial').length;
  const paidCount = orgs.filter(o => o.plan === 'active' || o.plan === 'founding').length;
  const total = trialCount + paidCount;
  const rate = total > 0 ? ((paidCount / total) * 100).toFixed(1) : '0.0';

  return (
    <div className="bg-white rounded-lg shadow p-5 flex flex-col items-center justify-center">
      <p className="text-xs font-semibold mb-3" style={{ color: '#89A8B1' }}>TRIAL → PAID CONVERSION</p>
      <p className="text-5xl font-bold" style={{ color: '#75BB2E' }}>{rate}%</p>
      <p className="text-xs mt-3" style={{ color: '#516469' }}>
        {paidCount} paid of {total} total orgs
      </p>
    </div>
  );
};

// ============================================
// TIME RANGE METRIC CARD
// ============================================

const TIME_RANGES = [
  { id: 'this_week', label: 'This Week' },
  { id: '7d', label: 'Past 7 Days' },
  { id: 'this_month', label: 'This Month' },
  { id: '30d', label: 'Past 30 Days' },
  { id: 'all', label: 'All Time' },
];

const getTimeRangeStart = (rangeId) => {
  const now = new Date();
  switch (rangeId) {
    case 'this_week': {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff);
      monday.setHours(0, 0, 0, 0);
      return monday;
    }
    case '7d': {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return d;
    }
    case 'this_month': {
      return new Date(now.getFullYear(), now.getMonth(), 1);
    }
    case '30d': {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d;
    }
    case 'all':
    default:
      return new Date(0);
  }
};

const TimeRangeMetricCard = ({ icon: Icon, label, color, items, dateField }) => {
  const [range, setRange] = useState('7d');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const start = getTimeRangeStart(range);
  const filtered = items.filter(item => {
    const val = item[dateField];
    if (!val) return false;
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d >= start;
  });

  const currentLabel = TIME_RANGES.find(r => r.id === range)?.label || 'Past 7 Days';

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: color + '15' }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <p className="text-xs font-semibold" style={{ color: '#89A8B1' }}>{label}</p>
        </div>
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border"
            style={{ borderColor: '#E2E8F0', color: '#516469' }}
          >
            {currentLabel}
            <ChevronDown className="w-3 h-3" />
          </button>
          {dropdownOpen && (
            <div
              className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border z-10"
              style={{ borderColor: '#E2E8F0', minWidth: '140px' }}
            >
              {TIME_RANGES.map((r) => (
                <button
                  key={r.id}
                  onClick={() => { setRange(r.id); setDropdownOpen(false); }}
                  className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                  style={{
                    color: range === r.id ? '#071D39' : '#516469',
                    fontWeight: range === r.id ? '600' : '400',
                    backgroundColor: range === r.id ? '#F0F4F5' : 'transparent',
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-2xl font-bold" style={{ color }}>{filtered.length}</p>
    </div>
  );
};

// ============================================
// VITALS DASHBOARD (main export)
// ============================================

const VitalsDashboard = () => {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  // Toast: { message, kind: 'success' | 'error' } | null
  const [toast, setToast] = useState(null);
  const showToast = (message, kind = 'success') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 2800);
  };

  const loadVitals = async () => {
    setLoading(true);
    try {
      const [allUsers, allOrgs] = await Promise.all([
        getAllUsers(),
        getAllOrganizations(),
      ]);
      setUsers(allUsers);
      setOrgs(allOrgs);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading vitals:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadVitals();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#516469' }} />
        <span className="ml-2" style={{ color: '#516469' }}>Loading vitals...</span>
      </div>
    );
  }

  const totalUsers = users.length;
  const totalOrgs = orgs.length;

  const trialOrgs = orgs.filter(o => o.plan === 'trial').length;
  const activeOrgs = orgs.filter(o => o.plan === 'active').length;
  const foundingOrgs = orgs.filter(o => o.plan === 'founding').length;
  const cancelledOrgs = orgs.filter(o => o.plan === 'cancelled').length;

  const mrr = orgs.reduce((sum, org) => {
    if (org.plan === 'trial' || org.plan === 'cancelled') return sum;
    const seats = org.seatCount || 1;
    const isFounder = org.plan === 'founding';
    const base = isFounder ? PRICING_CONFIG.founding100.lockedBasePrice : PRICING_CONFIG.basePrice;
    const perSeat = isFounder ? PRICING_CONFIG.founding100.lockedPerSeatPrice : PRICING_CONFIG.perSeatPrice;
    const additionalSeats = Math.max(0, seats - 1); // base price includes 1 seat
    return sum + base + (additionalSeats * perSeat);
  }, 0);
  const formatMRR = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const activeRecently = users.filter(u => {
    if (!u.lastLoginAt) return false;
    const lastLogin = u.lastLoginAt?.toDate ? u.lastLoginAt.toDate() : new Date(u.lastLoginAt);
    return lastLogin >= sevenDaysAgo;
  }).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold" style={{ color: '#071D39' }}>Platform Vitals</h2>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs" style={{ color: '#89A8B1' }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={loadVitals}
            className="flex items-center gap-1 px-3 py-1 rounded text-sm font-semibold"
            style={{ backgroundColor: '#071D39', color: '#FFFFFF' }}
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
      </div>

      {/* Row 1: Key stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard icon={Users} label="Total Users" value={totalUsers} color="#071D39" />
        <MetricCard icon={Building2} label="Total Organizations" value={totalOrgs} color="#516469" />
        <MetricCard icon={Clock} label="Active (7d)" value={activeRecently} color="#2B6CB0" />
        <MetricCard icon={CreditCard} label="Trial" value={trialOrgs} color="#D69E2E" />
      </div>

      {/* Row 2: Founding 100 dial + Conversion rate + Plan breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Founding100Dial count={foundingOrgs} />
        <ConversionRateCard orgs={orgs} />
        <MetricCard icon={CreditCard} label="Active" value={activeOrgs} color="#75BB2E" />
        <MetricCard icon={CreditCard} label="Cancelled" value={cancelledOrgs} color="#E53E3E" />
      </div>

      {/* MRR Section */}
      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-semibold" style={{ color: '#89A8B1' }}>MONTHLY RECURRING REVENUE</p>
            <p className="text-3xl font-bold" style={{ color: '#75BB2E' }}>{formatMRR(mrr)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs" style={{ color: '#89A8B1' }}>Projected ARR</p>
            <p className="text-lg font-bold" style={{ color: '#071D39' }}>{formatMRR(mrr * 12)}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(PRICING_CONFIG.statuses).map(([key, status]) => {
            const count = orgs.filter(o => o.plan === key).length;
            const seats = orgs.filter(o => o.plan === key).reduce((sum, o) => sum + (o.seatCount || 1), 0);
            const isFounder = key === 'founding';
            const base = isFounder ? PRICING_CONFIG.founding100.lockedBasePrice : PRICING_CONFIG.basePrice;
            const perSeat = isFounder ? PRICING_CONFIG.founding100.lockedPerSeatPrice : PRICING_CONFIG.perSeatPrice;
            const additionalSeats = Math.max(0, seats - count); // 1 included seat per org
            const revenue = (key === 'trial' || key === 'cancelled') ? 0 : (count * base) + (additionalSeats * perSeat);
            return (
              <div key={key} className="rounded-lg p-3" style={{ backgroundColor: status.color + '10', border: `1px solid ${status.color}30` }}>
                <p className="text-xs font-semibold" style={{ color: status.color }}>{status.label}</p>
                <p className="text-lg font-bold" style={{ color: '#071D39' }}>{count} <span className="text-xs font-normal" style={{ color: '#516469' }}>orgs</span></p>
                {(key === 'active' || key === 'founding') && (
                  <p className="text-xs" style={{ color: '#516469' }}>{seats} seats · {formatMRR(base)} base (1 seat incl.) + {formatMRR(perSeat)}/extra seat</p>
                )}
                <p className="text-sm font-semibold mt-1" style={{ color: status.color }}>{formatMRR(revenue)}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Row: Signups & Cancelled with time range dropdowns */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <TimeRangeMetricCard icon={TrendingUp} label="SIGNUPS" color="#2F855A" items={users} dateField="createdAt" />
        <TimeRangeMetricCard icon={AlertTriangle} label="CANCELLED" color="#E53E3E" items={orgs.filter(o => o.plan === 'cancelled')} dateField="cancelledAt" />
      </div>

      {/* User Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b" style={{ borderColor: '#E2E8F0' }}>
          <h3 className="font-semibold" style={{ color: '#071D39' }}>All Users</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="w-full text-sm" style={{ minWidth: '600px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F7FAFC' }}>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Name</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Email</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Role</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Signed Up</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const createdAt = u.createdAt?.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
                const lastLogin = u.lastLoginAt?.toDate ? u.lastLoginAt.toDate() : (u.lastLoginAt ? new Date(u.lastLoginAt) : null);
                return (
                  <tr key={u.id} className="border-t" style={{ borderColor: '#E2E8F0' }}>
                    <td className="px-4 py-2" style={{ color: '#071D39' }}>{u.firstName} {u.lastName}</td>
                    <td className="px-4 py-2" style={{ color: '#516469' }}>{u.email}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: u.platformRole === 'superAdmin' ? '#FED7D7' : '#E2E8F0',
                          color: u.platformRole === 'superAdmin' ? '#E53E3E' : '#516469',
                        }}>
                        {u.platformRole}
                      </span>
                    </td>
                    <td className="px-4 py-2" style={{ color: '#516469' }}>{createdAt.toLocaleDateString()}</td>
                    <td className="px-4 py-2" style={{ color: '#516469' }}>
                      {lastLogin ? lastLogin.toLocaleDateString() + ' ' + lastLogin.toLocaleTimeString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Org Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
        <div className="px-4 py-3 border-b" style={{ borderColor: '#E2E8F0' }}>
          <h3 className="font-semibold" style={{ color: '#071D39' }}>All Organizations</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="w-full text-sm" style={{ minWidth: '700px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F7FAFC' }}>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Name</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Plan</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Seats</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Founding 100</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Created</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: '#516469' }}>Org ID</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => {
                const createdAt = org.createdAt?.toDate ? org.createdAt.toDate() : new Date(org.createdAt);
                const planColors = {
                  trial:     { bg: '#FEFCBF', text: '#D69E2E' },
                  active:    { bg: '#C6F6D5', text: '#2F855A' },
                  founding:  { bg: '#D4AF3720', text: '#D4AF37' },
                  cancelled: { bg: '#FED7D7', text: '#E53E3E' },
                  comped:    { bg: '#E0E7FF', text: '#3730A3' },
                };
                return (
                  <tr key={org.id} className="border-t" style={{ borderColor: '#E2E8F0' }}>
                    <td className="px-4 py-2 font-semibold" style={{ color: '#071D39' }}>{org.name}</td>
                    <td className="px-4 py-2">
                      <PlanBadge
                        org={org}
                        planColors={planColors}
                        onChanged={loadVitals}
                        onToast={showToast}
                      />
                    </td>
                    <td className="px-4 py-2 font-semibold" style={{ color: '#071D39' }}>{org.seatCount || '—'}</td>
                    <td className="px-4 py-2">
                      {org.isFounding100 ? (
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#D4AF3720', color: '#D4AF37', border: '1px solid #D4AF3750' }}>
                            ★ #{org.foundingNumber}
                          </span>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Revoke Founding 100 status from ${org.name}?`)) {
                                const res = await revokeFoundingStatus(org.id);
                                if (res?.success) {
                                  const restored = res.restoredPlan || 'trial';
                                  showToast(
                                    `Revoked ${org.name} — plan restored to "${restored}".`
                                  );
                                  loadVitals();
                                } else {
                                  showToast(res?.reason || 'Could not revoke.', 'error');
                                }
                              }
                            }}
                            className="text-xs px-2 py-0.5 rounded"
                            style={{ color: '#E53E3E', backgroundColor: '#FEE2E2' }}
                          >
                            Revoke
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={async () => {
                            const result = await grantFoundingStatus(org.id);
                            if (result.success) {
                              showToast(
                                `Granted Founding #${result.foundingNumber} to ${org.name}.`
                              );
                              loadVitals();
                            } else {
                              showToast(result.reason || 'Could not grant.', 'error');
                            }
                          }}
                          className="text-xs px-2 py-1 rounded font-semibold"
                          style={{ color: '#805AD5', backgroundColor: '#805AD520', border: '1px solid #805AD540' }}
                        >
                          Grant Badge
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2" style={{ color: '#516469' }}>{createdAt.toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-xs font-mono" style={{ color: '#89A8B1' }}>{org.id}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast notifications (success/error) */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 9999,
            backgroundColor: toast.kind === 'error' ? '#FEE2E2' : '#DCFCE7',
            color: toast.kind === 'error' ? '#991B1B' : '#166534',
            border: '1px solid ' + (toast.kind === 'error' ? '#FCA5A5' : '#86EFAC'),
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            maxWidth: 360,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default VitalsDashboard;